import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Plus,
  Edit,
  Trash2,
  Package,
  Search,
  X,
} from "lucide-react";
import { IProduct } from "@/services/mocks/productData";
import { productService, posService, type IProductResponse, type IPOS } from "@/services";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ProductForm, {
  IProductFormData,
} from "@/components/business/products/ProductForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/useToast";

/**
 * Formatea el precio como moneda chilena
 *
 * @param price - Precio en pesos chilenos
 * @returns Precio formateado como string
 */
const formatPrice = (price: number): string => {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  }).format(price);
};

/**
 * Obtiene el color del badge según la categoría
 *
 * @param category - Categoría del producto
 * @returns Variante del badge
 */
const getCategoryBadgeVariant = (
  category: string
): "default" | "secondary" | "destructive" | "outline" => {
  switch (category) {
    case "Menú Principal":
      return "default";
    case "Bebidas":
      return "secondary";
    case "Snacks":
      return "outline";
    default:
      return "outline";
  }
};

/**
 * Obtiene las iniciales del nombre del producto para el avatar
 *
 * @param name - Nombre del producto
 * @returns Iniciales como string
 */
const getProductInitials = (name: string): string => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Página de gestión de productos para administradores
 *
 * Muestra una tabla con todos los productos del sistema,
 * incluyendo imagen, nombre, categoría, precio y disponibilidad.
 * Optimizada para dispositivos móviles con layout responsive.
 */
const ProductManagementPage: React.FC = () => {
  const [products, setProducts] = useState<IProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<IProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estados para el modal de creación
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Estados para el modal de edición
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<IProduct | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Estados para el diálogo de eliminación
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<IProduct | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { toast } = useToast();

  // Filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [posList, setPosList] = useState<IPOS[]>([]);
  const [selectedPos, setSelectedPos] = useState<string>("all");

  // Mover aquí la función loadProducts
  const loadProducts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response: IProductResponse = await productService.getAllProducts();
      setProducts(response.products);
      setFilteredProducts(response.products);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error al cargar productos";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  // Cargar lista de POS para filtro admin
  useEffect(() => {
    const loadPOS = async () => {
      try {
        const list = await posService.getPOSList();
        const active = list.filter((p) => p.activo !== false);
        setPosList(active);
      } catch (e) {
        console.error("Error cargando POS:", e);
      }
    };
    loadPOS();
  }, []);

  // Derivar categorías únicas basadas en el POS seleccionado
  const categories = [
    "all",
    ...Array.from(
      new Set(
        (
          selectedPos === "all"
            ? products
            : products.filter(
                (p) =>
                  Array.isArray((p as any).posIds) &&
                  (p as any).posIds.includes(Number(selectedPos))
              )
        ).map((p) => p.categoria)
      )
    ).filter(Boolean),
  ];

  // Aplicar filtros y búsqueda
  useEffect(() => {
    let list = products.slice();

    // Filtro por búsqueda (nombre/descripcion)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (p) =>
          p.nombre.toLowerCase().includes(term) ||
          (p.descripcion || "").toLowerCase().includes(term)
      );
    }

    // Filtro por categoría
    if (selectedCategory !== "all") {
      list = list.filter((p) => p.categoria === selectedCategory);
    }

    // Filtro por POS
    if (selectedPos !== "all") {
      const posId = Number(selectedPos);
      list = list.filter((p) => Array.isArray((p as any).posIds) && (p as any).posIds.includes(posId));
    }

    setFilteredProducts(list);
  }, [products, searchTerm, selectedCategory, selectedPos]);

  // Resetear categoría al cambiar POS para evitar categorías inválidas
  useEffect(() => {
    setSelectedCategory("all");
  }, [selectedPos]);

  /**
   * Maneja la acción de crear producto (placeholder)
   */
  const handleCreateProduct = () => {
    setIsCreateModalOpen(true);
  };

  /**
   * Cierra el modal de creación
   */
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  /**
   * Maneja el envío del formulario de creación
   */
  const handleSubmitCreate = async (data: IProductFormData) => {
    setIsCreating(true);
    try {
      console.log(" creating product data", data);
      await productService.createProduct(data);
      await loadProducts();
      setIsCreateModalOpen(false);
      toast({
        title: "Producto creado",
        description: `El producto "${data.nombre}" fue creado exitosamente.`,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error al crear producto";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Cierra el modal de edición
   */
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingProduct(null);
  };

  /**
   * Maneja el envío del formulario de edición
   */
  const handleSubmitEdit = async (data: IProductFormData) => {
    if (!editingProduct) return;

    setIsUpdating(true);
    try {
      await productService.updateProduct(editingProduct.id, data);
      await loadProducts();
      setIsEditModalOpen(false);
      setEditingProduct(null);
      toast({
        title: "Producto actualizado",
        description: `El producto "${data.nombre}" fue actualizado exitosamente.`,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error al actualizar producto";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Maneja la confirmación de eliminación
   */
  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    setIsDeleting(true);
    try {
      await productService.deleteProduct(productToDelete.id);
      await loadProducts();
      setIsDeleteDialogOpen(false);
      setProductToDelete(null);
      toast({
        title: "Producto eliminado",
        description: `El producto "${productToDelete.nombre}" fue eliminado exitosamente.`,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error al eliminar producto";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Maneja la cancelación de eliminación
   */
  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  /**
   * Maneja la acción de editar producto
   */
  const handleEditProduct = (product: IProduct) => {
    setEditingProduct(product);
    setIsEditModalOpen(true);
  };

  /**
   * Maneja la acción de eliminar producto
   */
  const handleDeleteProduct = (product: IProduct) => {
    setProductToDelete(product);
    setIsDeleteDialogOpen(true);
  };

  // Estado de carga
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[...Array(5)].map((_, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-4 p-4 border border-border rounded-lg"
                >
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-16" />
                  <div className="flex space-x-2">
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-16" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Estado de error
  if (!isLoading && error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-3xl font-bold text-foreground">
            Gestión de Productos
          </h1>
          <Button
            onClick={handleCreateProduct}
            className="flex items-center space-x-2 h-10"
          >
            <Plus className="h-4 w-4" />
            <span>Crear Producto</span>
          </Button>
        </div>

        <Alert variant="destructive" className="border-destructive/50">
          <Package className="h-4 w-4" />
          <AlertDescription className="text-destructive-foreground">
            Error al cargar productos: {error}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Gestión de Productos
          </h1>
          <p className="text-muted-foreground mt-1">
            Administra el catálogo de productos del sistema
          </p>
        </div>
        <Button
          onClick={handleCreateProduct}
          className="flex items-center space-x-2 h-10"
        >
          <Plus className="h-4 w-4" />
          <span>Crear Producto</span>
        </Button>
      </div>

      {/* Barra de búsqueda y filtros */}
      <div className="space-y-4">
        {/* Búsqueda */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar productos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-10"
          />
        </div>

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Filtro por categoría */}
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-56 h-10">
              <SelectValue placeholder="Todas las categorías" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c === "all" ? "Todas las categorías" : c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtro por POS */}
          <Select value={selectedPos} onValueChange={setSelectedPos}>
            <SelectTrigger className="w-full sm:w-56 h-10">
              <SelectValue placeholder="Todos los POS" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los POS</SelectItem>
              {posList.map((pos) => (
                <SelectItem key={pos.id} value={String(pos.id)}>
                  {pos.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Filtros activos */}
        {(searchTerm || selectedCategory !== "all" || selectedPos !== "all") && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground">Filtros activos:</span>
            {searchTerm && (
              <Badge variant="outline" className="flex items-center gap-1">
                Buscar: {searchTerm}
                <button
                  onClick={() => setSearchTerm("")}
                  className="ml-1 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {selectedCategory !== "all" && (
              <Badge variant="outline" className="flex items-center gap-1">
                Categoría: {selectedCategory}
                <button
                  onClick={() => setSelectedCategory("all")}
                  className="ml-1 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {selectedPos !== "all" && (
              <Badge variant="outline" className="flex items-center gap-1">
                POS: {posList.find((p) => String(p.id) === selectedPos)?.nombre ?? selectedPos}
                <button
                  onClick={() => setSelectedPos("all")}
                  className="ml-1 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Modal de creación de producto */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-lg overflow-y-auto max-h-[95vh]">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              Crear Producto
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Completa los campos para agregar un nuevo producto al catálogo.
            </DialogDescription>
          </DialogHeader>
          <ProductForm onSubmit={handleSubmitCreate} isLoading={isCreating} />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseCreateModal}
              disabled={isCreating}
              className="h-10"
            >
              Cancelar
            </Button>
            {/* El botón de guardar está dentro del formulario */}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de edición de producto */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              Editar Producto
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Modifica la información del producto {editingProduct?.nombre}.
            </DialogDescription>
          </DialogHeader>
          {editingProduct && (
            <ProductForm
              onSubmit={handleSubmitEdit}
              isLoading={isUpdating}
              initialData={{
                nombre: editingProduct.nombre,
                descripcion: editingProduct.descripcion,
                precio: editingProduct.precio,
                categoria: editingProduct.categoria,
                imagen: editingProduct.imagen,
                disponible: editingProduct.disponible,
                type: editingProduct.type,
                pack_size: editingProduct.pack_size,
                posIds: (editingProduct as any).posIds,
              }}
            />
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseEditModal}
              disabled={isUpdating}
              className="h-10"
            >
              Cancelar
            </Button>
            {/* El botón de guardar está dentro del formulario */}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog para confirmación de eliminación */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              ¿Estás seguro de que quieres eliminar este producto?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Esta acción es permanente. El producto{" "}
              <strong className="text-foreground">
                {productToDelete?.nombre}
              </strong>{" "}
              será eliminado del catálogo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleCancelDelete}
              disabled={isDeleting}
              className="h-10"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="h-10"
            >
              {isDeleting ? "Eliminando..." : "Sí, eliminar producto"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tabla de Productos */}
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Package className="h-5 w-5" />
            Productos ({filteredProducts.length}{products.length !== filteredProducts.length ? ` de ${products.length}` : ""})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredProducts.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                No se encontraron productos
              </p>
            </div>
          ) : (
            <>
              {/* Vista Desktop - Tabla */}
              <div className="hidden md:block">
                <div className="rounded-md border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow key="products-header-row">
                        <TableHead className="w-[80px]">Imagen</TableHead>
                        <TableHead className="w-[300px]">
                          Nombre del Producto
                        </TableHead>
                        <TableHead className="w-[150px]">Categoría</TableHead>
                        <TableHead className="w-[120px]">Precio</TableHead>
                        <TableHead className="w-[120px]">Disponible</TableHead>
                        <TableHead className="w-[150px] text-right">
                          Acciones
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredProducts.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell>
                            <Avatar className="h-10 w-10 border-2 border-border">
                              <AvatarImage
                                src={product.imagen}
                                alt={product.nombre}
                              />
                              <AvatarFallback className="bg-primary/10 text-primary">
                                {getProductInitials(product.nombre)}
                              </AvatarFallback>
                            </Avatar>
                          </TableCell>

                          <TableCell>
                            <div>
                              <div className="font-semibold text-foreground">
                                {product.nombre}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                ID: {product.id.slice(0, 8)}...
                              </div>
                            </div>
                          </TableCell>

                          <TableCell>
                            <Badge
                              variant={getCategoryBadgeVariant(
                                product.categoria
                              )}
                              className="text-xs"
                            >
                              {product.categoria}
                            </Badge>
                          </TableCell>

                          <TableCell>
                            <span className="font-mono text-sm text-foreground">
                              {formatPrice(product.precio)}
                            </span>
                          </TableCell>

                          <TableCell>
                            <Badge
                              variant={
                                product.disponible ? "default" : "destructive"
                              }
                              className="text-xs"
                            >
                              {product.disponible ? "Sí" : "No"}
                            </Badge>
                          </TableCell>

                          <TableCell className="text-right">
                            <div className="flex items-center space-x-2 justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditProduct(product)}
                                className="flex items-center space-x-1 h-8"
                              >
                                <Edit className="h-3 w-3" />
                                <span>Editar</span>
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteProduct(product)}
                                className="flex items-center space-x-1 h-8"
                              >
                                <Trash2 className="h-3 w-3" />
                                <span>Eliminar</span>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Vista Mobile - Cards */}
              <div className="md:hidden space-y-4">
                      {filteredProducts.map((product) => (
                  <Card
                    key={product.id}
                    className="hover:shadow-sm transition-shadow"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <Avatar className="h-16 w-16 border-2 border-border flex-shrink-0">
                          <AvatarImage
                            src={product.imagen}
                            alt={product.nombre}
                          />
                          <AvatarFallback className="bg-primary/10 text-primary text-lg">
                            {getProductInitials(product.nombre)}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div className="min-w-0 flex-1">
                              <h3 className="font-semibold text-foreground truncate">
                                {product.nombre}
                              </h3>
                              <p className="text-xs text-muted-foreground">
                                ID: {product.id.slice(0, 8)}...
                              </p>
                            </div>
                            <Badge
                              variant={
                                product.disponible ? "default" : "destructive"
                              }
                              className="text-xs flex-shrink-0"
                            >
                              {product.disponible
                                ? "Disponible"
                                : "No disponible"}
                            </Badge>
                          </div>

                          <div className="flex items-center gap-2 mb-3">
                            <Badge
                              variant={getCategoryBadgeVariant(
                                product.categoria
                              )}
                              className="text-xs"
                            >
                              {product.categoria}
                            </Badge>
                            <span className="font-mono text-sm font-semibold text-foreground">
                              {formatPrice(product.precio)}
                            </span>
                          </div>

                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditProduct(product)}
                              className="flex-1 h-10"
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Editar
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteProduct(product)}
                              className="flex-1 h-10"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Eliminar
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductManagementPage;
